import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, AlertCircle, CheckCircle, Image as ImageIcon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { searchBelgianCompany, CompanySearchResult, CompanySearchResponse } from "@/utils/company-search"; // Import new types
import { toast } from "@/hooks/use-toast";
import { useSettings } from "@/providers/SettingsProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTranslation } from "react-i18next";

// Define LogoResult type locally if not exported from utils
interface LogoResult {
  brandId: string;
  name: string;
  icon: string;
}

interface CompanySearchProps {
  onCompanyFound: (company: CompanySearchResult) => void;
}

export const CompanySearch = ({ onCompanyFound }: CompanySearchProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<CompanySearchResponse | null>(null);
  const [selectedCompanyIndex, setSelectedCompanyIndex] = useState<number | null>(null);
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);
  const { devMode } = useSettings();
  const { t } = useTranslation();

  const resetSearch = () => {
    setSearchTerm("");
    setSearchResults(null);
    setSelectedCompanyIndex(null);
    setSelectedLogoUrl(null);
    setSearchError(null);
  };

  const searchCompany = async () => {
    if (!searchTerm.trim()) {
      toast({ title: "Search term required", description: "Please enter a company name or VAT number", variant: "destructive" });
      return;
    }

    setSearching(true);
    setSearchError(null);
    setSearchResults(null); // Clear previous results
    setSelectedCompanyIndex(null);
    setSelectedLogoUrl(null);

    toast({ title: "Searching for company", description: "Looking up company information..." });

    try {
      const results = await searchBelgianCompany(searchTerm);

      if (results && (results.potentialCompanies.length > 0 || results.logos.length > 0)) {
        toast({ title: "Search Complete", description: `Found ${results.potentialCompanies.length} potential companies and ${results.logos.length} logos.` });
        setSearchResults(results);
        // Pre-select if only one result
        if (results.potentialCompanies.length === 1) {
          setSelectedCompanyIndex(0);
        }
        if (results.logos.length === 1) {
          setSelectedLogoUrl(results.logos[0].icon);
        }
      } else {
        toast({ title: "Not Found", description: "No companies or logos found.", variant: "destructive" });
        setSearchError("No companies or logos found with the provided details.");
      }
    } catch (error) {
      console.error("Error searching company:", error);
      const errorMessage = error instanceof Error ? error.message : "Unknown error during search";
      toast({ title: "Search Failed", description: errorMessage, variant: "destructive" });
      setSearchError(errorMessage);
    } finally {
      setSearching(false);
    }
  };

  const handleSelectCompany = (index: number) => {
    setSelectedCompanyIndex(index);
  };

  const handleSelectLogo = (url: string) => {
    setSelectedLogoUrl(url);
  };

  const confirmSelection = () => {
    if (selectedCompanyIndex !== null && searchResults) {
      const selectedCompany = searchResults.potentialCompanies[selectedCompanyIndex];
      onCompanyFound({
        ...selectedCompany,
        logo_url: selectedLogoUrl || undefined, // Use selected logo or undefined
        country: "Belgium" // Assuming Belgium, adjust if needed
      });
      // Optionally reset the search component after selection
      // resetSearch(); 
    } else {
      toast({ title: "Selection Incomplete", description: "Please select a company.", variant: "destructive" });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchCompany();
    }
  };

  return (
    <div className="mb-4 pb-4 border-b space-y-4">
      <div>
        <Label htmlFor="company-search-input" className="block text-sm font-medium text-gray-700 mb-2">
          {t("companies.companySearch.title")}
        </Label>
        <div className="flex gap-2">
          <Input
            id="company-search-input"
            placeholder={t("companies.companySearch.placeholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
            disabled={searching}
          />
          <Button
            type="button"
            onClick={searchCompany}
            disabled={searching || !searchTerm.trim()}
            className="bg-[#5a7ebf] hover:bg-[#5a7ebf]/90"
          >
            {searching ? t("companies.companySearch.searching") : <><Search className="mr-2 h-4 w-4" /> {t("companies.companySearch.button")}</>}
          </Button>
        </div>
        {devMode && <p className="text-xs text-orange-500 mt-1">Dev mode: Mock data fallback enabled.</p>}
      </div>

      {searchError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{searchError}</AlertDescription>
        </Alert>
      )}

      {searchResults && (
        <div className="space-y-4">
          {/* Company Selection */}
          {searchResults.potentialCompanies.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Company ({searchResults.potentialCompanies.length} found)</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-40">
                  <RadioGroup
                    value={selectedCompanyIndex?.toString()} // RadioGroup expects string value
                    onValueChange={(value) => handleSelectCompany(parseInt(value, 10))}
                  >
                    {searchResults.potentialCompanies.map((company, index) => (
                      <div key={company.vat_number || index} className="flex items-center space-x-2 py-2 border-b last:border-b-0">
                        <RadioGroupItem value={index.toString()} id={`company-${index}`} />
                        <Label htmlFor={`company-${index}`} className="flex-1 cursor-pointer">
                          <div className="font-medium">{company.name}</div>
                          <div className="text-sm text-gray-600">
                            {company.vat_number && <span className="mr-2">VAT: {company.vat_number}</span>}
                            {[company.street, company.postal_code, company.city].filter(Boolean).join(", ")}
                          </div>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Logo Selection */}
          {searchResults.logos.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Select Logo ({searchResults.logos.length} found)</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-32">
                  <RadioGroup
                    value={selectedLogoUrl || ""} // Handle null case
                    onValueChange={handleSelectLogo}
                    className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2"
                  >
                    {searchResults.logos.map((logo) => (
                      <Label
                        key={logo.brandId}
                        htmlFor={`logo-${logo.brandId}`}
                        className={`relative flex flex-col items-center justify-center p-2 border rounded-sm cursor-pointer hover:border-digger-blue ${selectedLogoUrl === logo.icon ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                      >
                        <RadioGroupItem value={logo.icon} id={`logo-${logo.brandId}`} className="sr-only" />
                        <img src={logo.icon} alt={`${logo.name} logo`} className="h-12 w-12 object-contain mb-1" />
                        <span className="text-xs text-center truncate w-full">{logo.name}</span>
                      </Label>
                    ))}
                    {/* Option to select no logo */}
                    <Label
                      htmlFor="logo-none"
                      className={`relative flex flex-col items-center justify-center p-2 border rounded-sm cursor-pointer hover:border-digger-blue ${selectedLogoUrl === null ? 'border-digger-blue ring-2 ring-digger-blue' : ''}`}
                    >
                      <RadioGroupItem value="" id="logo-none" className="sr-only" />
                      <div className="h-12 w-12 flex items-center justify-center bg-gray-100 rounded mb-1">
                        <ImageIcon className="h-6 w-6 text-gray-400" />
                      </div>
                      <span className="text-xs text-center">No Logo</span>
                    </Label>
                  </RadioGroup>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Confirmation Button */}
          {(selectedCompanyIndex !== null || selectedLogoUrl !== null) && (
             <div className="flex justify-end space-x-2">
               <Button variant="outline" onClick={resetSearch}>Cancel</Button>
               <Button 
                 onClick={confirmSelection} 
                 disabled={selectedCompanyIndex === null} // Must select a company
                 className="bg-green-600 hover:bg-green-700"
                 type="button"
               >
                 <CheckCircle className="mr-2 h-4 w-4" />
                 Confirm Selection
               </Button>
             </div>
          )}
        </div>
      )}
    </div>
  );
};
